import { 
  Ticket,
  Brain,
  Users,
  MessageSquare,
  BarChart3,
  Shield,
  Smartphone,
  Globe,
  Zap,
  Search,
  Clock,
  Headphones,
  Bot,
  Settings,
  Database,
  Network,
  CheckCircle,
  Target,
  Activity,
  Layers,
  Workflow,
  BookOpen,
  Mail,
  Phone,
  MessagesSquare,
  TrendingUp,
  Lock,
  Eye,
  Gauge,
  ListChecks,
  UserCog,
  ClipboardCheck,
  Sparkles,
  LayoutDashboard,
  FileText,
  AlertTriangle,
  Bell,
  HelpCircle,
  Link,
  Share2,
  Rocket
} from 'lucide-react';
import type { ProductFeatureConfig } from './types';

export const itServiceDeskPlatformConfig: ProductFeatureConfig = {
  keyBenefits: [
    {
      icon: Ticket,
      title: "Unified Omnichannel Ticketing System",
      description: "Consolidate support requests from email, chat, phone, Slack, Microsoft Teams, WhatsApp, and social media into a single unified workspace with full conversation history.",
      metrics: "10+ channels, zero context loss",
      color: "from-violet-500 to-purple-600"
    },
    {
      icon: Brain,
      title: "AI-Powered Intent Detection & Classification",
      description: "Intelligent AI that automatically classifies tickets by intent, sentiment, priority, and type, then routes them to the right agent based on skills and availability.",
      metrics: "95% classification accuracy",
      color: "from-blue-500 to-indigo-600"
    },
    {
      icon: Bot,
      title: "AI Copilot for Agent Productivity",
      description: "Smart AI assistant that suggests replies, recommends macros, summarizes conversations, and surfaces relevant knowledge base articles in real-time.",
      metrics: "40% faster resolution times",
      color: "from-cyan-500 to-blue-600"
    },
    {
      icon: BookOpen,
      title: "Self-Service Portal & Knowledge Base",
      description: "Branded self-service portal with AI-powered search, interactive FAQs, community forums, and intelligent chatbot to deflect common queries automatically.",
      metrics: "60% ticket deflection rate",
      color: "from-emerald-500 to-teal-600"
    },
    {
      icon: Workflow,
      title: "Visual Workflow Automation Builder",
      description: "No-code workflow builder for creating custom automation rules, trigger-based actions, SLA escalations, and multi-level approval processes.",
      metrics: "500+ automation triggers",
      color: "from-orange-500 to-red-600"
    },
    {
      icon: BarChart3,
      title: "Real-Time Analytics & Insights",
      description: "Live dashboards with ticket metrics, CSAT/NPS tracking, agent performance analytics, SLA compliance monitoring, and AI-powered trend forecasting.",
      metrics: "Real-time visibility, AI insights",
      color: "from-pink-500 to-rose-600"
    },
    {
      icon: Users,
      title: "Customer 360° View & Context",
      description: "Complete customer profile with interaction history, previous tickets, account details, and CRM data surfaced automatically for every conversation.",
      metrics: "Full context in seconds",
      color: "from-indigo-500 to-purple-600"
    },
    {
      icon: Clock,
      title: "SLA Management & Escalation Engine",
      description: "Automated SLA tracking with breach alerts, priority-based escalation rules, and intelligent load balancing to ensure service level compliance.",
      metrics: "99.5% SLA compliance",
      color: "from-amber-500 to-orange-600"
    },
    {
      icon: Headphones,
      title: "Omnichannel Agent Desktop",
      description: "Unified agent workspace handling tickets, live chat, phone calls, and social messages with integrated voice, screen sharing, and cobrowsing.",
      metrics: "Single pane of glass",
      color: "from-teal-500 to-cyan-600"
    },
    {
      icon: Globe,
      title: "Multi-Brand & Multi-Language Support",
      description: "Support multiple brands, products, and regions from a single platform with localized portals, language detection, and AI-powered translation.",
      metrics: "50+ languages supported",
      color: "from-purple-500 to-indigo-600"
    },
    {
      icon: Link,
      title: "1000+ App Integrations & Marketplace",
      description: "Pre-built integrations with Salesforce, HubSpot, Slack, Teams, Jira, and 1000+ other apps plus REST APIs and webhooks for custom development.",
      metrics: "Connect any system",
      color: "from-sky-500 to-blue-600"
    },
    {
      icon: MessageSquare,
      title: "AI Chatbot with Natural Language",
      description: "Intelligent conversational AI chatbot with natural language understanding, contextual responses, and seamless handoff to live agents when needed.",
      metrics: "24/7 automated support",
      color: "from-rose-500 to-pink-600"
    }
  ],
  
  advancedCapabilities: [
    {
      icon: Sparkles,
      title: "AI-Powered Ticket Summarization",
      description: "Automatic AI summarization of long ticket threads, highlighting key issues, customer sentiment, and recommended actions for faster agent onboarding.",
      metrics: "70% faster ticket review",
      color: "from-yellow-500 to-orange-600"
    },
    {
      icon: TrendingUp,
      title: "Predictive Issue Detection",
      description: "Machine learning algorithms that identify trending issues, predict ticket volume spikes, and surface potential problems before they escalate.",
      metrics: "Proactive issue prevention",
      color: "from-green-500 to-emerald-600"
    },
    {
      icon: Gauge,
      title: "Real-Time Sentiment Monitoring",
      description: "Live sentiment analysis across all channels detecting frustrated customers, escalating conversations, and opportunities for service recovery.",
      metrics: "Real-time mood detection",
      color: "from-red-500 to-pink-600"
    },
    {
      icon: UserCog,
      title: "Skills-Based Intelligent Routing",
      description: "Advanced routing engine that matches tickets to agents based on skills, language, expertise, workload, and historical performance.",
      metrics: "First-contact resolution boost",
      color: "from-blue-500 to-cyan-600"
    },
    {
      icon: ClipboardCheck,
      title: "Change & Service Request Management",
      description: "ITIL-aligned change management with approval workflows, impact assessment, rollback procedures, and service catalog integration.",
      metrics: "ITIL best practices",
      color: "from-indigo-500 to-violet-600"
    },
    {
      icon: LayoutDashboard,
      title: "Custom Dashboard & Report Builder",
      description: "Drag-and-drop dashboard builder with custom KPIs, scheduled report delivery, data export, and role-based views for stakeholders.",
      metrics: "Unlimited customization",
      color: "from-purple-500 to-indigo-600"
    },
    {
      icon: AlertTriangle,
      title: "Proactive Outage Alerts & Status Pages",
      description: "Automated incident communication with status page integration, proactive customer notifications, and incident ticket auto-creation.",
      metrics: "Reduce inbound volume",
      color: "from-orange-500 to-red-600"
    },
    {
      icon: Share2,
      title: "Side Conversations & Collaboration",
      description: "Internal collaboration tools for involving other teams, external vendors, or specialists without exposing private discussions to customers.",
      metrics: "Seamless teamwork",
      color: "from-teal-500 to-green-600"
    },
    {
      icon: Rocket,
      title: "Macro Library & Response Templates",
      description: "Shared macro library with AI-suggested responses, dynamic placeholders, rich formatting, and team-specific template management.",
      metrics: "Consistent, fast replies",
      color: "from-cyan-500 to-blue-600"
    }
  ],
  
  securityPerformance: [
    {
      icon: Target,
      title: 'SOC 2 Type II & ISO 27001 Certified',
      description: 'Enterprise-grade security certifications with annual audits, penetration testing, and continuous compliance monitoring',
      metrics: 'Complete regulatory compliance',
      color: 'from-indigo-500 to-purple-600'
    },
    {
      icon: Lock,
      title: 'End-to-End Data Encryption',
      description: 'AES-256 encryption for data at rest and TLS 1.3 for data in transit with customer-managed encryption key options',
      metrics: 'Military-grade encryption',
      color: 'from-green-500 to-emerald-600'
    },
    {
      icon: Shield,
      title: 'GDPR, HIPAA & PCI-DSS Ready',
      description: 'Pre-built compliance controls for healthcare, financial services, and EU data protection with data processing agreements',
      metrics: 'Industry compliance built-in',
      color: 'from-blue-500 to-indigo-600'
    },
    {
      icon: Eye,
      title: 'Role-Based Access Control (RBAC)',
      description: 'Granular permission system with custom roles, field-level security, IP restrictions, and session management',
      metrics: 'Zero-trust security model',
      color: 'from-purple-500 to-indigo-600'
    },
    {
      icon: Activity,
      title: 'Comprehensive Audit Trails',
      description: 'Complete logging of all user actions, data changes, API calls, and system events with tamper-proof audit records',
      metrics: 'Full audit visibility',
      color: 'from-yellow-500 to-amber-600'
    },
    {
      icon: Database,
      title: 'Data Residency & Regional Hosting',
      description: 'Choose data center locations for compliance with regional data sovereignty requirements including EU, UK, US, and APAC',
      metrics: 'Global data compliance',
      color: 'from-emerald-500 to-green-600'
    },
    {
      icon: Network,
      title: 'SSO & Multi-Factor Authentication',
      description: 'Enterprise SSO integration with SAML 2.0, OAuth, LDAP, plus MFA with authenticator apps, SMS, and hardware tokens',
      metrics: 'Enterprise identity management',
      color: 'from-orange-500 to-red-600'
    },
    {
      icon: Rocket,
      title: '99.99% Uptime SLA Guarantee',
      description: 'Enterprise-grade infrastructure with auto-scaling, global CDN, disaster recovery, and financially-backed uptime guarantees',
      metrics: '99.99% availability',
      color: 'from-cyan-500 to-blue-600'
    },
    {
      icon: CheckCircle,
      title: 'Continuous Security Monitoring',
      description: 'Real-time threat detection, vulnerability scanning, DDoS protection, and 24/7 security operations center monitoring',
      metrics: 'Proactive threat prevention',
      color: 'from-red-500 to-pink-600'
    }
  ]
};
